import { useTranslation } from "react-i18next";
import { squadLabel } from "../../../utils/archeryConfig.js";

/**
 * 순마다 온 데이터를 세션(같은 시각에 같이 쏘는 조 묶음)별로 한 번만 정리
 * - 30/70/90m, 2025 145m: 두 조가 한 과녁(관)을 나눠 씀. 먼저 만든 조(JO_ID 작은 쪽)가 먼저, 다른 조가 다음
 * - 145m(2026~): 순마다 한 조. 같은 관 선수를 사대 배정 순서로 세워 앞 절반이 먼저, 뒤 절반이 다음
 * - 자리: 같은 조·과녁(먼저/다음) 안의 사대 배정 순서
 * - 본선(byGender): 남녀 조가 같은 시각·같은 조 번호라 남자/여자 세션으로 나눔
 * order: "first"(먼저) / "next"(다음) / "" (같이 쏘는 조 없음)
 */
export const buildSessions = (timeSlots, event, year, byGender = false) => {
  const hasTurns = event === "145" && Number(year) >= 2026;
  const groups = new Map();
  timeSlots.forEach((timeSlot) => {
    const time = timeSlot.round_schedule?.round_time;
    timeSlot.rounds.forEach(({ group, players, joId }) => {
      if (!players.length) return;
      // 조 ID로 구분 (본선은 남녀 조 번호가 같음)
      const key = joId || group;
      if (!groups.has(key)) groups.set(key, { group, joId, times: [], players });
      groups.get(key).times.push(time);
    });
  });

  groups.forEach(({ players }) => {
    const byTarget = {};
    players.forEach((p) => (byTarget[p.target] = byTarget[p.target] || []).push(p));
    Object.values(byTarget).forEach((list) => {
      // 사대 배정 순서가 없는 서버면 받은 순서 그대로
      const ordered = list.every((p) => p.targetJoinId)
        ? [...list].sort((a, b) => a.targetJoinId - b.targetJoinId)
        : list;
      const half = Math.ceil(ordered.length / 2);
      ordered.forEach((p, i) => {
        if (hasTurns) {
          p.order = i < half ? "first" : "next";
          p.seat = i < half ? i + 1 : i - half + 1;
        } else {
          p.seat = i + 1;
        }
        // 서버 자리 번호가 있으면 그대로 (과녁에 혼자 남은 90m 선수 등)
        if (p.serverSeat) p.seat = p.serverSeat;
      });
    });
  });

  const sessions = new Map();
  [...groups.values()]
    .sort((a, b) => a.joId - b.joId || Number(a.group) - Number(b.group))
    .forEach((info) => {
      const gender = byGender ? info.players[0].gender : "";
      const key = `${info.times.join("|")}|${gender}`;
      if (!sessions.has(key))
        sessions.set(key, { times: info.times, gender, groups: [] });
      sessions.get(key).groups.push(info);
    });

  // 같은 시각에 같은 과녁(관)을 나눠 쓰는 조끼리 JO_ID 작은 조가 먼저, 다른 조가 다음
  // 2025 30/70/90m는 세 조가 두 조씩 번갈아 쏴서 시각마다 순서가 다를 수 있음 → timeOrders(시각별)로 제목에 표시
  if (!hasTurns) {
    const targetsOf = (info) => new Set(info.players.map((p) => p.target));
    groups.forEach((info) => {
      const mine = targetsOf(info);
      info.timeOrders = info.times.map((time) => {
        const sharing = [...groups.values()].filter(
          (other) =>
            other.times.includes(time) &&
            [...targetsOf(other)].some((target) => mine.has(target))
        );
        if (sharing.length < 2) return "";
        return sharing.every((other) => other.joId >= info.joId) ? "first" : "next";
      });
      const orders = new Set(info.timeOrders.filter(Boolean));
      const order = orders.size === 1 ? [...orders][0] : "";
      info.players.forEach((p) => (p.order = order));
      if (orders.size < 2) info.timeOrders = null;
    });
  }

  return [...sessions.values()].sort((a, b) =>
    a.times[0].localeCompare(b.times[0])
  );
};

// 세션 하나: 조·순 시각 제목 + 과녁(관)별 표
const TargetsView = ({ event, year, session }) => {
  const { t, i18n } = useTranslation();
  const targetName = t("targets.target");

  const getGenderLabel = (gender) => {
    const genderCode = gender === "W" ? "F" : gender;

    if (genderCode === "M") {
      return t("common.m");
    } else if (genderCode === "F") {
      return t("common.f");
    }
    return genderCode;
  };

  const date = session.times[0]?.slice(0, 10);
  // 시각마다 순서가 바뀌는 조(2025)는 시각 옆에 먼저/다음 표시
  const timeOrders =
    session.groups.length === 1 ? session.groups[0].timeOrders : null;
  const times = session.times
    .map((time, i) => {
      const order = timeOrders?.[i];
      return time.slice(11, 16) + (order ? ` ${t(`targets.${order}`)}` : "");
    })
    .join(" · ");
  const hasOrder = session.groups.some(({ players }) =>
    players.some((p) => p.order)
  );
  // 조 이름: 예선은 공지 이름(A-1 등), 본선(남녀로 나눈 세션)은 조 번호 그대로
  const squadName = (group) => (session.gender ? group : squadLabel(year, group));
  const squads = session.groups
    .map(({ group }) => squadName(group))
    .join("/");

  // 과녁별 선수: 먼저 → 다음, 자리 순
  const byTarget = {};
  session.groups.forEach(({ group, players }) =>
    players.forEach((p) =>
      (byTarget[p.target] = byTarget[p.target] || []).push({ ...p, group })
    )
  );
  const targets = Object.keys(byTarget).sort((a, b) => Number(a) - Number(b));
  if (targets.length === 0) return null;

  // 한국어: "1과녁" / "1관", 영어: "Target 1"
  const targetTitle = (target) =>
    i18n.language === "ko" ? `${target}${targetName}` : `${targetName} ${target}`;

  return (
    <div key={i18n.language} className="time-slot-section">
      <div className="table-title">
        {date} ·{" "}
        {session.gender && `${getGenderLabel(session.gender)} · `}
        {t("targets.squad")} {squads} · {times}
      </div>
      <div className="table-flex">
        {targets.map((target) => {
          const players = byTarget[target].sort(
            (a, b) =>
              (a.order === "next") - (b.order === "next") || a.seat - b.seat
          );
          return (
            <div key={target} className="table-box box-2">
              <div className="table-title">{targetTitle(target)}</div>
              <div className="table-wrap">
                <div className="table-shadow">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("table.country")}</th>
                        <th>{t("table.player-name")}</th>
                        <th>{t("common.gender")}</th>
                        <th>{t("targets.squad")}</th>
                        {hasOrder && <th>{t("targets.order")}</th>}
                        <th>{targetName}</th>
                        <th>{t("targets.seat")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {players.map((player) => (
                        <tr key={`${target}-${player.id}`}>
                          <td>{player.country}</td>
                          <td>{player.name}</td>
                          <td>{getGenderLabel(player.gender)}</td>
                          <td>{squadName(player.group)}</td>
                          {hasOrder && (
                            <td>
                              {player.order ? t(`targets.${player.order}`) : ""}
                            </td>
                          )}
                          <td style={{ color: "red" }}>{player.target}</td>
                          <td>{player.seat}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="table-gradient"></div>
              </div>
            </div>
          );
        })}
      </div>
      <br />
      <br />
    </div>
  );
};
export default TargetsView;
